/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineSpeakerphone,
  HiOutlineTrash,
} from "react-icons/hi";

import ListPage from "../../../layout/ListPage";
import type { Action, Column } from "../../../ui";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { pushOneAdvertisement } from "../../../lib/sysNavigation/SysNavigation";
import { _TYPES_ } from "../utils/types";
import { _PAGE_SIZE_ } from "../../../lib/other/tools";

type Advertisement = {
  dosee_ads_id: number | string;
  title?: string;
  start_date?: string;
  [key: string]: unknown;
};

const addRoute = "/data/advertisement/add";
const editRoute = "/data/advertisement/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const ctx: any = useDoSeeDataContext();
  const { advertisements, sponsorships, fetchSponsorships, deleteOneContent, setDataState } = ctx;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!sponsorships) fetchSponsorships();
  }, [sponsorships]);

  const list: Advertisement[] | null = useMemo(() => {
    if (!sponsorships?.data) return null;
    return sponsorships.data as Advertisement[];
  }, [sponsorships]);

  const rows = useMemo<Advertisement[] | null>(() => {
    if (!Array.isArray(list)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) =>
      String(row.title ?? "").toLowerCase().includes(q),
    );
  }, [list, query]);

  const deleteOne = async (row: Advertisement) => {
    const { status } = await deleteOneContent({
      data_id: row.dosee_ads_id,
      content: _TYPES_.ADV,
    });
    if (status === 200 && Array.isArray(advertisements)) {
      setDataState({
        data: advertisements.filter(
          (adv: Advertisement) => adv.dosee_ads_id !== row.dosee_ads_id,
        ),
        content: _TYPES_.ADV,
      });
    }
  };

  const columns: Column<Advertisement>[] = [
    {
      header: "Title",
      cell: (row) => (
        <span className="font-medium text-gray-900">{row.title ?? "—"}</span>
      ),
    },
    {
      header: "Start date",
      cell: (row) => (
        <span className="text-gray-600">{row.start_date ?? "—"}</span>
      ),
    },
  ];

  const rowActions = (row: Advertisement): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneAdvertisement(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => deleteOne(row),
      confirm: {
        title: `Delete ${row.title ?? "this advertisement"}?`,
        description: "This advertisement will be permanently removed.",
        confirmLabel: "Delete",
      },
    },
  ];

  const pageActions: Action[] = [
    { label: "Add advertisement", variant: "primary", href: addRoute },
    {
      label: "Back",
      icon: <HiOutlineArrowLeft className="h-3.5 w-3.5" />,
      onClick: () => navigate("/dosee/data"),
    },
  ];

  return (
    <ListPage<Advertisement>
      title="Advertisements"
      subtitle="Sponsored placements shown on the public site."
      icon={<HiOutlineSpeakerphone className="h-5 w-5" />}
      actions={pageActions}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.dosee_ads_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search advertisements…",
      }}
      pagination={{
        page,
        pageSize,
        total: sponsorships?.count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchSponsorships();
        },
      }}
      empty="No advertisements yet."
    />
  );
};

export default Overview;
